/**
 * API Service with authentication headers
 *
 * Security:
 * - Always gets fresh token from Firebase for each request
 * - Never sends client_id in requests (backend extracts from token)
 * - Handles 401 responses appropriately
 */
import { config } from '../config/environment';
import { getFirebaseAuth } from './firebase';
import type { ApiResponse, AuthMeResponse, User, Client, Report, UploadUrlResponse, EmbedResponse } from '../types';

// Mock data for development without backend
const mockUser: User = {
  id: 'mock-user-id',
  email: 'test@example.com',
  firstName: 'Test',
  lastName: 'User',
  clientId: 'mock-client-id',
  role: 'admin',
  createdAt: new Date().toISOString(),
};

const mockClient: Client = {
  id: 'mock-client-id',
  name: 'Test Client',
  color: '#0D6433',
  logo: '',
  createdAt: new Date().toISOString(),
};

/**
 * Get authentication headers with fresh Firebase token
 * SECURITY: Always get fresh token, never cache
 */
const getAuthHeaders = async (): Promise<HeadersInit> => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  try {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;

    if (user) {
      // Get fresh token (Firebase handles refresh automatically)
      const token = await user.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (error) {
    console.error('Failed to get auth token:', error);
  }

  return headers;
};

/**
 * Generic API request handler
 */
const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  // Use mock data in development if enabled
  if (config.features.useMock) {
    return handleMockRequest<T>(endpoint, options);
  }

  try {
    const headers = await getAuthHeaders();
    const url = `${config.api.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    // Handle non-OK responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Special handling for 401 - token expired or invalid
      if (response.status === 401) {
        // Trigger re-auth flow (handled by AuthContext)
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }

      return {
        error: errorData.error || `Request failed: ${response.status}`,
      };
    }

    const data = await response.json();
    return { data };

  } catch (error) {
    console.error('API request failed:', error);
    return {
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
};

/**
 * Handle mock requests for development
 */
const handleMockRequest = async <T>(
  endpoint: string,
  _options: RequestInit
): Promise<ApiResponse<T>> => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 500));

  if (endpoint === '/auth/me') {
    return {
      data: { user: mockUser, client: mockClient } as T,
    };
  }

  if (endpoint === '/reports') {
    return {
      data: [] as T,
    };
  }

  return { error: 'Mock endpoint not implemented' };
};

/**
 * API endpoints
 */
export const api = {
  /**
   * Auth endpoints
   */
  auth: {
    /**
     * Get current authenticated user with client data
     * Called after Firebase auth to verify user exists in DB
     */
    me: (): Promise<ApiResponse<AuthMeResponse>> =>
      apiRequest<AuthMeResponse>('/auth/me'),
  },

  /**
   * Reports endpoints
   * Note: client_id is NEVER sent - backend extracts from token
   */
  reports: {
    getAll: async (): Promise<ApiResponse<Report[]>> => {
      const res = await apiRequest<{ reports: Report[] }>('/reports');
      if (res.error) return { error: res.error };
      return { data: res.data?.reports || [] };
    },

    getById: (id: string): Promise<ApiResponse<Report>> =>
      apiRequest<Report>(`/reports/${id}`),

    /**
     * Get a secure embed token for viewing a report
     * Returns a proxy URL that expires in 5 minutes
     */
    getEmbed: async (reportId: string): Promise<ApiResponse<EmbedResponse>> => {
      const res = await apiRequest<EmbedResponse>(`/reports/${reportId}/embed`);
      if (res.error) return { error: res.error };
      return { data: res.data };
    },
  },

  /**
   * Admin endpoints (require admin role)
   */
  admin: {
    // Clients
    getClients: async (): Promise<ApiResponse<Client[]>> => {
      const res = await apiRequest<{ clients: Client[] }>('/clients');
      if (res.error) return { error: res.error };
      return { data: res.data?.clients || [] };
    },

    createClient: async (data: Omit<Client, 'id' | 'createdAt'>): Promise<ApiResponse<Client>> => {
      const res = await apiRequest<{ client: Client }>('/clients', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (res.error) return { error: res.error };
      return { data: res.data?.client };
    },

    updateClient: async (id: string, data: Partial<Client>): Promise<ApiResponse<Client>> => {
      const res = await apiRequest<{ client: Client }>(`/clients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (res.error) return { error: res.error };
      return { data: res.data?.client };
    },

    deleteClient: (id: string): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/clients/${id}`, {
        method: 'DELETE',
      }),

    // Users
    getUsers: async (): Promise<ApiResponse<User[]>> => {
      const res = await apiRequest<{ users: User[] }>('/users');
      if (res.error) return { error: res.error };
      return { data: res.data?.users || [] };
    },

    createUser: async (data: Omit<User, 'id' | 'createdAt'>): Promise<ApiResponse<User>> => {
      const res = await apiRequest<{ user: User }>('/users', {
        method: 'POST',
        body: JSON.stringify({
          email: data.email,
          client_id: data.clientId,
          first_name: data.firstName,
          last_name: data.lastName,
          phone: data.phone,
          role: data.role,
        }),
      });
      if (res.error) return { error: res.error };
      return { data: res.data?.user };
    },

    updateUser: async (id: string, data: Partial<User>): Promise<ApiResponse<User>> => {
      const res = await apiRequest<{ user: User }>(`/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...(data.email && { email: data.email }),
          ...(data.clientId && { client_id: data.clientId }),
          ...(data.firstName && { first_name: data.firstName }),
          ...(data.lastName && { last_name: data.lastName }),
          ...(data.phone !== undefined && { phone: data.phone }),
          ...(data.role !== undefined && { role: data.role }),
        }),
      });
      if (res.error) return { error: res.error };
      return { data: res.data?.user };
    },

    deleteUser: (id: string): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/users/${id}`, {
        method: 'DELETE',
      }),

    // Reports
    getReports: async (): Promise<ApiResponse<Report[]>> => {
      const res = await apiRequest<{ reports: Report[] }>('/reports');
      if (res.error) return { error: res.error };
      return { data: res.data?.reports || [] };
    },

    createReport: async (data: Omit<Report, 'id' | 'createdAt'>): Promise<ApiResponse<Report>> => {
      const res = await apiRequest<{ report: Report }>('/reports', {
        method: 'POST',
        body: JSON.stringify({
          client_id: data.clientId,
          name: data.name,
          iframe_src: data.iframeSrc,
          order: data.order,
        }),
      });
      if (res.error) return { error: res.error };
      return { data: res.data?.report };
    },

    updateReport: async (id: string, data: Partial<Report>): Promise<ApiResponse<Report>> => {
      const res = await apiRequest<{ report: Report }>(`/reports/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...(data.clientId && { client_id: data.clientId }),
          ...(data.name && { name: data.name }),
          ...(data.iframeSrc && { iframe_src: data.iframeSrc }),
          ...(data.order !== undefined && { order: data.order }),
        }),
      });
      if (res.error) return { error: res.error };
      return { data: res.data?.report };
    },

    deleteReport: (id: string): Promise<ApiResponse<void>> =>
      apiRequest<void>(`/reports/${id}`, {
        method: 'DELETE',
      }),

    // Uploads
    getUploadUrl: (filename: string, contentType: string): Promise<ApiResponse<UploadUrlResponse>> =>
      apiRequest<UploadUrlResponse>(
        `/uploads/presigned-url?filename=${encodeURIComponent(filename)}&content_type=${encodeURIComponent(contentType)}`
      ),

    // Viewer Permissions
    getViewerPermissions: async (viewerId: string): Promise<ApiResponse<{ permissions: string[] }>> => {
      const res = await apiRequest<{ permissions: string[] }>(`/viewers/${viewerId}/permissions`);
      if (res.error) return { error: res.error };
      return { data: { permissions: res.data?.permissions || [] } };
    },

    updateViewerPermissions: async (viewerId: string, reportIds: string[]): Promise<ApiResponse<{ permissions: string[] }>> => {
      const res = await apiRequest<{ permissions: string[] }>(`/viewers/${viewerId}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ report_ids: reportIds }),
      });
      if (res.error) return { error: res.error };
      return { data: res.data };
    },
  },
};
